import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from 'store';
import apiClient from 'utils/apiClient';

export interface MemberSearchParams {
    page?: number;
    perPage?: number;
    totalSearchKeyword?: string;
    groupIds?: string[];
    teamIds?: string[];
    id?: number;
    accountDetailUuid?: string;
    name?: string;
    email?: string;
    teamId?: number;
    status?: string[];
    rank?: number[];
    createdStartDate?: string;
    createdEndDate?: string;
    createdStartDateTime?: string;
    createdEndDateTime?: string;
    lastSeenStartDate?: string;
    lastSeenEndDate?: string;
    lastSeenStartDateTime?: string;
    lastSeenEndDateTime?: string;
    orderTarget?: string;
    orderType?: string;
}

export interface MemberItem {
    id: number;
    name: string;
    email: string;
    accountDetailUuid: string;
    photoUrl: string;
    status: string;
    teamId: number;
    createdAt: string;
    updatedAt: string;
    lastSeen: string | null;
    team: { id: number; name: string; createdAt: string; updatedAt: string } | null;
    rank: { name: string; level: number; createdAt: string; updatedAt: string } | null;
}

export interface MemberDetailData {
    member: {
        id: number;
        name: string;
        teamId: number;
        email: string;
        accountDetailUuid: string;
        department: string;
        position: string;
        duty: string;
        rankLevel: number;
        rankName: string;
        status: string;
        joinedAt: string;
        createdAt: string;
        updatedAt: string;
        photoUrl: string;
        type: string;
        topics: any[];
        topicCount: number;
        groupChats: any[];
        groupChatCount: number;
        profile: {
            email: string;
            phoneNumber: string;
            position: string;
            positionId: number;
            department: string;
            statusMessage: string;
        };
        rank: any;
        team: any;
    };
    lastSeen: {
        id: number;
        lastSeen: string | null;
    };
}

export interface DailyUsage {
    date: string;
    text: number;
    post: number;
    comment: number;
    sticker: number;
    jandiFileCount: number;
    albumFileCount: number;
    driveFileCount: number;
    jandiFileSize: number;
    albumFileSize: number;
    driveFileSize: number;
    connectCount: number;
    messageTotalCount: number;
    fileTotalCount: number;
    fileTotalSize: number;
}

export interface MemberState {
    members: MemberItem[];
    totalCount: number;
    page: number;
    perPage: number;
    loading: boolean;
    detail: MemberDetailData | null;
    detailLoading: boolean;
    usages: DailyUsage[];
    usageLoading: boolean;
}

export const initialState: MemberState = {
    members: [],
    totalCount: 0,
    page: 1,
    perPage: 20,
    loading: false,
    detail: null,
    detailLoading: false,
    usages: [],
    usageLoading: false,
};

export const searchMembers = createAsyncThunk<
    { list: MemberItem[]; totalCount: number; page: number; perPage: number },
    MemberSearchParams,
    { state: RootState }
>(
    'member/search',
    async (params, { rejectWithValue }) => {
        try {
            const queryParams = new URLSearchParams();
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    if (Array.isArray(value)) {
                        value.forEach(v => queryParams.append(key, String(v)));
                    } else {
                        queryParams.append(key, String(value));
                    }
                }
            });

            const response = await apiClient.get(`/api/member/search?${queryParams.toString()}`);
            return response.data;
        } catch (e: any) {
            return rejectWithValue(e?.response?.data?.message || 'Failed to search members');
        }
    },
    {
        condition: (_, { getState }) => {
            const { loading } = (getState() as RootState).member as MemberState;
            return !loading;
        }
    }
);

export const getMemberDetail = createAsyncThunk<MemberDetailData, number, { state: RootState }>(
    'member/detail',
    async (memberId, { rejectWithValue }) => {
        try {
            const response = await apiClient.get(`/api/member/${memberId}`);
            return response.data;
        } catch (e: any) {
            return rejectWithValue(e?.response?.data?.message || 'Failed to get member detail');
        }
    }
);

export const getMemberUsage = createAsyncThunk<
    DailyUsage[],
    { memberId: number; teamId: number; startAt: string; endAt: string; offsetTime?: string },
    { state: RootState }
>(
    'member/usage',
    async (params, { rejectWithValue }) => {
        try {
            const { memberId, teamId, startAt, endAt, offsetTime = '+09:00' } = params;
            const response = await apiClient.get(
                `/api/member/${memberId}/usage?teamId=${teamId}&startAt=${startAt}&endAt=${endAt}&offsetTime=${encodeURIComponent(offsetTime)}`
            );
            return response.data;
        } catch (e: any) {
            return rejectWithValue(e?.response?.data?.message || 'Failed to get usage data');
        }
    }
);

const memberSlice = createSlice({
    name: 'member',
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(searchMembers.pending, (state) => {
                state.loading = true;
            })
            .addCase(searchMembers.fulfilled, (state, action) => {
                state.loading = false;
                const incomingPage = action.payload.page;
                if (incomingPage === 1) {
                    // Fresh search: replace
                    state.members = action.payload.list;
                } else if (incomingPage === state.page + 1) {
                    // Sequential page: append
                    state.members = [...state.members, ...action.payload.list];
                } else {
                    // Out-of-order page: discard to prevent mix
                    return;
                }
                state.totalCount = action.payload.totalCount;
                state.page = incomingPage;
                state.perPage = action.payload.perPage;
            })
            .addCase(searchMembers.rejected, (state) => {
                state.loading = false;
            })
            .addCase(getMemberDetail.pending, (state) => {
                state.detailLoading = true;
            })
            .addCase(getMemberDetail.fulfilled, (state, action) => {
                state.detailLoading = false;
                state.detail = action.payload;
            })
            .addCase(getMemberDetail.rejected, (state) => {
                state.detailLoading = false;
            })
            .addCase(getMemberUsage.pending, (state) => {
                state.usageLoading = true;
            })
            .addCase(getMemberUsage.fulfilled, (state, action) => {
                state.usageLoading = false;
                state.usages = action.payload;
            })
            .addCase(getMemberUsage.rejected, (state) => {
                state.usageLoading = false;
            });
    },
    reducers: {
        resetMember: () => initialState,
        clearDetail: (state) => {
            state.detail = null;
            state.usages = [];
        },
    }
});

export const { resetMember, clearDetail } = memberSlice.actions;
export default memberSlice.reducer;
